import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Alert, Empty, Input, Modal, Select, Table, Tag, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem } from "../../../../Components/Items";

/**
 * TEMPORAL, uso interno. Copia la biometría del reloj (huellas, rostros y contraseña) de los
 * empleados de esta empresa a los mismos empleados que ya existen en otra empresa del usuario
 * (erp_backend/Controllers/Empleados/copiarBiometria.controllers.js).
 *
 * Al elegir la empresa destino el backend propone las parejas (RFC, CURP, NSS o nombre idéntico) y
 * dice qué recibiría cada uno; en destino sólo se agrega lo que le falta. No manda nada a los
 * relojes.
 *
 * Props: visible, onCerrar()
 */

const normalizar = (texto) =>
  String(texto ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const codigoTexto = (codigo) => (codigo !== null && codigo !== undefined ? String(codigo).padStart(4, "0") : "—");

const textoBiometria = (r) => {
  const partes = [];
  if (r?.huellas) partes.push(`${r.huellas} ${r.huellas === 1 ? "huella" : "huellas"}`);
  if (r?.rostros) partes.push(`${r.rostros} ${r.rostros === 1 ? "rostro" : "rostros"}`);
  if (r?.password) partes.push("contraseña");
  return partes.join(" · ") || "—";
};

// Las que se copian primero; después lo que necesita atención y al final lo que ya está.
const ORDEN_ESTADO = { copiar: 0, conflicto: 1, ambiguo: 2, sinPareja: 3, yaLaTiene: 4 };

const ESTADO_TAG = {
  conflicto: { color: "red", texto: "Datos distintos" },
  ambiguo: { color: "orange", texto: "Ambiguo" },
  sinPareja: { color: "default", texto: "Sin pareja" },
  yaLaTiene: { color: "green", texto: "Ya la tiene" },
};

const ESTADO_INICIAL = {
  empresaDestinoId: undefined,
  empresaDestino: null,
  filas: [],
  seleccionados: [],
  busqueda: "",
  error: null,
};

class ModalCopiarBiometria extends Component {
  state = {
    cargando: false,
    evaluando: false,
    enviando: false,
    empresas: [],
    ...ESTADO_INICIAL,
  };
  // Descarta la respuesta de una empresa que ya no es la elegida.
  consultaVigente = 0;

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ ...ESTADO_INICIAL });
      this.consultarEmpresas();
    }
  }

  async consultarEmpresas() {
    this.setState({ cargando: true, empresas: [] });
    try {
      const res = await axios.post(
        "empleados/datosCopiaBiometria",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      );
      if (res.data.success !== 2) {
        this.setState({ cargando: false });
        message.error(res.data.mensaje || "No se pudieron consultar las empresas", 8);
        return;
      }
      this.setState({ cargando: false, empresas: res.data.empresas || [] });
    } catch (e) {
      this.setState({ cargando: false });
      message.error("Error de conexión al consultar las empresas");
    }
  }

  async evaluar(empresaDestinoId) {
    const consulta = ++this.consultaVigente;
    this.setState({ evaluando: true, error: null, empresaDestinoId, empresaDestino: null, filas: [], seleccionados: [] });
    try {
      const res = await axios.post(
        "empleados/evaluarCopiaBiometria",
        { empresaId: this.props.empresaId, empresaDestinoId },
        { headers: this.props.headersToken }
      );
      if (consulta !== this.consultaVigente) return;
      if (res.data.success !== 2) {
        this.setState({ evaluando: false, error: res.data.mensaje || "No se pudo revisar la copia" });
        return;
      }
      const filas = (res.data.filas || [])
        .map((f) => ({ ...f, key: String(f.empleadoId) }))
        .sort((a, b) => ORDEN_ESTADO[a.estado] - ORDEN_ESTADO[b.estado] || a.nombre.localeCompare(b.nombre, "es"));
      this.setState({
        evaluando: false,
        empresaDestino: res.data.empresaDestino,
        filas,
        seleccionados: filas.filter((f) => f.estado === "copiar").map((f) => f.key),
      });
    } catch (e) {
      if (consulta !== this.consultaVigente) return;
      this.setState({ evaluando: false, error: "Error de conexión al revisar la copia" });
    }
  }

  cerrar() {
    if (this.state.enviando) return;
    this.props.onCerrar();
  }

  copiar = async () => {
    const { empresaDestinoId, seleccionados } = this.state;
    this.setState({ enviando: true });
    try {
      const res = await axios.post(
        "empleados/copiarBiometria",
        { empresaId: this.props.empresaId, empresaDestinoId, empleadosIds: seleccionados },
        { headers: this.props.headersToken }
      );
      this.setState({ enviando: false });
      if (res.data.success !== 2) {
        message.error(res.data.mensaje || "No se pudo copiar la biometría", 8);
        return;
      }
      message.success(res.data.mensaje || "Copia terminada");
      this.mostrarResultado(res.data);
      this.evaluar(empresaDestinoId);
    } catch (e) {
      this.setState({ enviando: false });
      message.error("Error de conexión al copiar la biometría");
    }
  };

  mostrarResultado(datos) {
    const copiados = datos.copiados || [];
    const omitidos = datos.omitidos || [];
    Modal.info({
      title: "Copia de biometría terminada",
      width: 620,
      content: (
        <div>
          <p>{datos.mensaje}</p>
          {copiados.length > 0 && (
            <ul>
              {copiados.map((c) => (
                <li key={c.empleadoId}>
                  <b>{c.destinoNombre}</b>: {textoBiometria(c.agrega)}
                </li>
              ))}
            </ul>
          )}
          {omitidos.length > 0 && (
            <>
              <p style={{ marginBottom: 4 }}>No se copiaron:</p>
              <ul style={{ color: "#cf1322" }}>
                {omitidos.map((o) => <li key={o.empleadoId}><b>{o.nombre}</b>: {o.motivo}</li>)}
              </ul>
            </>
          )}
          <p style={{ color: "#595959" }}>
            Para que llegue a los relojes de la empresa destino, usa "Sincronizar" en Asignación de Relojes.
          </p>
        </div>
      ),
    });
  }

  filtrarPorBusqueda(filas) {
    const texto = normalizar(this.state.busqueda.trim());
    if (!texto) return filas;
    return filas.filter((f) =>
      [codigoTexto(f.codigo), f.nombre, codigoTexto(f.destinoCodigo), f.destinoNombre].some((campo) => normalizar(campo).includes(texto))
    );
  }

  renderResultado(f) {
    if (f.estado === "copiar") return <span style={{ color: "#389e0d" }}>Se agrega: {textoBiometria(f.agrega)}</span>;
    const tag = ESTADO_TAG[f.estado] || { color: "default", texto: f.estado };
    return (
      <span>
        <Tag color={tag.color}>{tag.texto}</Tag>
        <span style={{ color: f.estado === "conflicto" ? "#cf1322" : "#8c8c8c" }}>{f.motivo}</span>
      </span>
    );
  }

  render() {
    const { cargando, evaluando, enviando, empresas, empresaDestinoId, empresaDestino, filas, seleccionados, busqueda, error } = this.state;
    const aCopiar = filas.filter((f) => f.estado === "copiar").length;
    const nombreDestino = empresaDestino?.nombre || "destino";

    const columnas = [
      {
        title: "Esta empresa",
        key: "origen",
        width: 230,
        render: (_, f) => <span>{codigoTexto(f.codigo)} · {f.nombre}</span>,
      },
      { title: "Tiene", key: "tiene", width: 170, render: (_, f) => textoBiometria(f.tiene) },
      {
        title: `En ${nombreDestino}`,
        key: "destino",
        width: 230,
        render: (_, f) => (f.destinoNombre ? <span>{codigoTexto(f.destinoCodigo)} · {f.destinoNombre}</span> : "—"),
      },
      { title: "Coincide por", key: "por", width: 110, render: (_, f) => (f.por ? <Tag color="blue">{f.por}</Tag> : "—") },
      { title: "Resultado", key: "resultado", render: (_, f) => this.renderResultado(f) },
    ];

    return (
      <ModalItem
        title="Copiar biometría a otra empresa"
        visible={this.props.visible}
        spinning={cargando || evaluando || enviando}
        onCancel={() => this.cerrar()}
        paddingTop
        width={window.innerWidth > 1100 ? 1050 : "95%"}
        destroyOnClose
      >
        <p style={{ color: "#595959", marginBottom: 8 }}>
          Copia huellas, rostros y contraseña del reloj a los mismos empleados en otra empresa. Se emparejan por RFC,
          CURP o NSS y, si no hay, por nombre idéntico. En destino sólo se agrega lo que le falta.
        </p>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Empresa destino</div>
        <Select
          showSearch
          style={{ width: "100%", marginBottom: 12 }}
          placeholder={empresas.length ? "Elige la empresa a la que se copia" : "No tienes acceso a otra empresa"}
          value={empresaDestinoId}
          optionFilterProp="label"
          onChange={(valor) => this.evaluar(valor)}
          options={empresas.map((e) => ({
            value: e._id,
            label: `${e.nombre}${e.rfc ? ` (${e.rfc})` : ""}${e.vencida ? " · suscripción vencida" : ""}`,
            disabled: e.vencida,
          }))}
        />

        {error && <Alert type="error" showIcon style={{ marginBottom: 10 }} message={error} />}

        {empresaDestino && (
          <>
            <Input.Search
              allowClear
              style={{ marginBottom: 10 }}
              placeholder="Buscar por código o nombre"
              value={busqueda}
              onChange={(e) => this.setState({ busqueda: e.target.value })}
            />
            <div style={{ overflowX: "auto" }}>
              <Table
                size="small"
                rowKey="key"
                dataSource={this.filtrarPorBusqueda(filas)}
                columns={columnas}
                pagination={{ pageSize: 50, showSizeChanger: false }}
                scroll={{ x: 1000, y: 380 }}
                locale={{ emptyText: <Empty description="Ningún empleado activo de esta empresa tiene biometría" /> }}
                rowSelection={{
                  selectedRowKeys: seleccionados,
                  preserveSelectedRowKeys: true,
                  getCheckboxProps: (f) => ({ disabled: f.estado !== "copiar" }),
                  onChange: (llaves) => this.setState({ seleccionados: llaves }),
                }}
              />
            </div>
            <p style={{ marginTop: 6, color: "#595959" }}>
              {`${aCopiar} de ${filas.length} con biometría tienen algo que copiar. `}
              No se manda nada a los relojes: para eso usa "Sincronizar" en Asignación de Relojes de {nombreDestino}.
            </p>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, marginBottom: 10 }}>
          <ButtonItem htmlType="button" blanco onClick={() => this.cerrar()}>
            Cerrar
          </ButtonItem>
          <ButtonItem
            htmlType="button"
            type="primary"
            icon={<CopyOutlined />}
            loading={enviando}
            disabled={!empresaDestino || !seleccionados.length || evaluando || enviando}
            onClick={this.copiar}
          >
            {`Copiar biometría (${seleccionados.length})`}
          </ButtonItem>
        </div>
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalCopiarBiometria);
